import {
  defineExperiment,
  type CustomExperimentSpec,
  type PanelRow,
} from '@learnforge/demo-render/harness';
import type { SetsContext, SetsKey } from '../types';
import { product, triple } from '../sets';
import { createPairGrid, type PairGridHandle } from '../render/pair-grid';

// ── State ─────────────────────────────────────────────────────────────────────

export interface State {
  A: string[];
  B: string[];
  highlight: Set<string>;
  mode: 'AxB' | 'A2' | 'AxBxC';
  advanced: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

// Alphabet pools: A uses lowercase letters; B uses uppercase letters; C uses digits
const ALPHA_A = ['a', 'b', 'c', 'd', 'e', 'f'];
const ALPHA_B = ['x', 'y', 'z', 'w', 'u', 'v'];
const ALPHA_C = ['1', '2', '3', '4', '5', '6'];

// ── Pure helpers ──────────────────────────────────────────────────────────────

function makeA(size: number): string[] {
  return ALPHA_A.slice(0, Math.max(1, size));
}

function makeB(size: number): string[] {
  return ALPHA_B.slice(0, Math.max(1, size));
}

function makeC(size: number): string[] {
  return ALPHA_C.slice(0, Math.max(1, size));
}

// ── Mode options ──────────────────────────────────────────────────────────────

const MODE_OPTIONS_BASIC: { value: State['mode']; labelKey: SetsKey }[] = [
  { value: 'AxB', labelKey: 'mode.AxB' },
];

const MODE_OPTIONS_ADVANCED: { value: State['mode']; labelKey: SetsKey }[] = [
  { value: 'AxB', labelKey: 'mode.AxB' },
  { value: 'A2', labelKey: 'mode.A2' },
  { value: 'AxBxC', labelKey: 'mode.AxBxC' },
];

// ── Spec ──────────────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<State, SetsKey, SetsContext> {
  return {
    labelKey: 'experiment.cartesian-product.label',
    demoId: 'sets',
    advancedKey: 'cartesian-product',

    initialState(ctx): State {
      return {
        A: makeA(3),
        B: makeB(4),
        highlight: new Set<string>(),
        mode: 'AxB',
        advanced: ctx.advanced,
      };
    },

    panel(state, ctx): PanelRow<SetsKey, State>[] {
      const advanced = ctx.advanced;
      const modeOptions = advanced ? MODE_OPTIONS_ADVANCED : MODE_OPTIONS_BASIC;

      const rows: PanelRow<SetsKey, State>[] = [
        {
          kind: 'slider',
          id: 'size-a',
          labelKey: 'param.size-a',
          min: 1,
          max: 6,
          step: 1,
          value: state.A.length,
          onChange: (v) => ({
            ...state,
            A: makeA(v),
            highlight: new Set<string>(),
          }),
        },
        {
          kind: 'slider',
          id: 'size-b',
          labelKey: 'param.size-b',
          min: 1,
          max: 6,
          step: 1,
          value: state.B.length,
          onChange: (v) => ({
            ...state,
            B: makeB(v),
            highlight: new Set<string>(),
          }),
        },
        {
          kind: 'advanced-toggle',
          labelKey: 'btn.advanced',
        },
      ];

      if (advanced) {
        rows.splice(rows.length - 1, 0, {
          kind: 'dropdown',
          id: 'mode',
          labelKey: 'param.mode',
          options: modeOptions,
          value: state.mode,
          onChange: (v) => ({
            ...state,
            mode: v as State['mode'],
            highlight: new Set<string>(),
          }),
        });
      }

      return rows;
    },

    createRender(_ctx, host, controls) {
      // ── Container ──
      const container = document.createElement('div');
      container.className = 'cartesian-product-container';
      host.appendChild(container);

      // ── SVG canvas ──
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'cartesian-product-svg');
      svg.setAttribute('viewBox', '0 0 500 400');
      svg.setAttribute('width', '500');
      svg.setAttribute('height', '400');
      container.appendChild(svg);

      // ── Readout ──
      const readout = document.createElement('div');
      readout.className = 'cartesian-product-readout';
      container.appendChild(readout);

      // ── Grid handles ──
      let gridHandle: PairGridHandle | null = null;

      // Auxiliary grids for AxBxC mode (one per C element)
      const auxGrids: PairGridHandle[] = [];

      function destroyGrids(): void {
        gridHandle?.destroy();
        gridHandle = null;
        for (const g of auxGrids) {
          g.destroy();
        }
        auxGrids.length = 0;
      }

      function update(state: State): void {
        const { A, B, highlight, mode } = state;
        const C = makeC(3);

        if (mode === 'AxB') {
          // ── Standard A×B grid ──
          if (gridHandle === null) {
            destroyGrids();
            gridHandle = createPairGrid(svg, {
              rows: A,
              cols: B,
              highlight,
              diagonal: false,
              onToggle(key) {
                controls.setState((prev) => {
                  const next = new Set(prev.highlight);
                  if (next.has(key)) {
                    next.delete(key);
                  } else {
                    next.add(key);
                  }
                  return { ...prev, highlight: next };
                });
              },
            });
          } else {
            gridHandle.update({ rows: A, cols: B, highlight, diagonal: false });
          }

          const count = product(A, B).length;
          readout.textContent = `A = {${A.join(',')}}   B = {${B.join(',')}}   |A×B| = |A|·|B| = ${A.length}·${B.length} = ${count}`;
        } else if (mode === 'A2') {
          // ── A² = A×A grid with diagonal marked ──
          if (gridHandle === null) {
            destroyGrids();
            gridHandle = createPairGrid(svg, {
              rows: A,
              cols: A,
              highlight,
              diagonal: true,
              onToggle(key) {
                controls.setState((prev) => {
                  const next = new Set(prev.highlight);
                  if (next.has(key)) {
                    next.delete(key);
                  } else {
                    next.add(key);
                  }
                  return { ...prev, highlight: next };
                });
              },
            });
          } else {
            gridHandle.update({ rows: A, cols: A, highlight, diagonal: true });
          }

          const count = product(A, A).length;
          readout.textContent = `A = {${A.join(',')}}   |A²| = |A|² = ${A.length}² = ${count}`;
        } else {
          // ── A×B×C: one A×B sub-grid per element of C ──
          // Destroy old single grid if it exists
          gridHandle?.destroy();
          gridHandle = null;

          const triples = triple(A, B, C);

          // We render one small labelled sub-group per c∈C
          // Rebuild all aux grids
          for (const g of auxGrids) {
            g.destroy();
          }
          auxGrids.length = 0;

          const SVG_NS = 'http://www.w3.org/2000/svg';
          // Group wrapper per slice
          for (let ci = 0; ci < C.length; ci++) {
            const cEl = C[ci] as string;

            // Create a translated sub-group for this slice
            const sliceOffsetY = ci * 130;
            const sliceG = document.createElementNS(SVG_NS, 'g');
            sliceG.setAttribute('transform', `translate(0,${sliceOffsetY})`);
            sliceG.setAttribute('class', 'pg-slice');
            svg.appendChild(sliceG);

            // Label for c element
            const label = document.createElementNS(SVG_NS, 'text');
            label.setAttribute('class', 'pg-slice-label');
            label.setAttribute('x', '5');
            label.setAttribute('y', '20');
            label.setAttribute('dominant-baseline', 'central');
            label.textContent = `c = ${cEl}`;
            sliceG.appendChild(label);

            // Sub-grid: keys are "row:col" but we prefix with c to make them globally unique
            const highlightSlice = new Set<string>();
            for (const key of highlight) {
              // Keys for this mode are stored as "a:x:1" style
              const parts = key.split(':');
              if (parts[2] === cEl) {
                // Map to local pair key
                highlightSlice.add(`${parts[0]}:${parts[1]}`);
              }
            }

            const innerG = document.createElementNS(SVG_NS, 'g');
            innerG.setAttribute('transform', 'translate(60,5)');
            sliceG.appendChild(innerG);

            const handle = createPairGrid(innerG, {
              rows: A,
              cols: B,
              highlight: highlightSlice,
              onToggle(pairKey) {
                const [row, col] = pairKey.split(':');
                const tripleKey = `${row}:${col}:${cEl}`;
                controls.setState((prev) => {
                  const next = new Set(prev.highlight);
                  if (next.has(tripleKey)) {
                    next.delete(tripleKey);
                  } else {
                    next.add(tripleKey);
                  }
                  return { ...prev, highlight: next };
                });
              },
            });
            auxGrids.push(handle);
          }

          readout.textContent =
            `A = {${A.join(',')}}   B = {${B.join(',')}}   C = {${C.join(',')}}   ` +
            `|A×B×C| = |A|·|B|·|C| = ${A.length}·${B.length}·${C.length} = ${triples.length}`;
        }
      }

      return {
        update,
        dispose() {
          destroyGrids();
        },
      };
    },

    applyShotState(state): State {
      // Shot pose: A of size 3 × B of size 4, mode=AxB, a few cells highlighted.
      const A = makeA(3);
      const B = makeB(4);
      return {
        ...state,
        A,
        B,
        mode: 'AxB',
        highlight: new Set(['a:x', 'b:y', 'c:z']),
        advanced: false,
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
